import React, { useEffect } from "react";
import styled from "styled-components";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";

import { Nav } from "../components/Nav";
import Footer from "../components/Footer";
import P from "../components/P";
import { BLOG_POSTS } from "../data/blogPosts";
import {
    SITE_URL,
    absoluteUrl,
    setMetaTag,
    setLinkTag,
    setStructuredData,
    removeStructuredData,
    restoreDefaultMeta
} from "../utils/pageMeta";

const BLOG_URL = `${SITE_URL}/blog`;
const BLOG_TITLE = "Blog Mango | Automação, software sob medida e eficiência operacional";
const BLOG_DESCRIPTION = "Guias práticos da Mango sobre automação de processos, software sob medida, PCP, operação digital e tecnologia para empresas que precisam crescer com controle.";
const BLOG_KEYWORDS = "automação de processos, software sob medida, eficiência operacional, PCP, RPA, transformação digital, Mango Software House";
const CAPTURE_URL = "/#diagnostico";

const Page = styled.main`
    width: 100%;
    padding: 132px 0 112px;
    overflow-x: hidden;

    @media only screen and (max-width: 720px) {
        padding: 112px 0 136px;
    }
`;

const Shell = styled.div`
    width: min(1080px, calc(100vw - 32px));
    margin: 0 auto;

    @media only screen and (max-width: 640px) {
        width: min(100%, calc(100vw - 24px));
    }
`;

const Hero = styled.header`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 360px);
    gap: 40px;
    align-items: end;
    padding: 24px 0 42px;
    border-bottom: 1px solid rgba(32, 23, 53, 0.12);

    @media only screen and (max-width: 860px) {
        grid-template-columns: 1fr;
        align-items: start;
        gap: 24px;
        padding-bottom: 30px;
    }
`;

const HeroCopy = styled.div`
    display: grid;
    gap: 18px;
    max-width: 780px;
`;

const Kicker = styled.p`
    margin: 0;
    width: fit-content;
    padding: 8px 12px;
    border-radius: 999px;
    color: var(--brand-2);
    background: #f2ecfb;
    font-size: 0.86rem;
    font-weight: 800;
    letter-spacing: 0;
`;

const Title = styled.h1`
    margin: 0;
    color: var(--ink);
    font-family: var(--font-display);
    font-size: 3.45rem;
    line-height: 1.02;
    letter-spacing: 0;
    text-wrap: balance;

    @media only screen and (max-width: 720px) {
        font-size: 2.15rem;
        line-height: 1.08;
    }
`;

const Summary = styled.p`
    margin: 0;
    color: var(--muted);
    max-width: 720px;
    font-size: 1.08rem;
    font-weight: 500;
    line-height: 1.72;
`;

const HeroCta = styled.aside`
    display: grid;
    gap: 14px;
    padding: 22px;
    border: 1px solid rgba(32, 23, 53, 0.12);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 18px 48px rgba(32, 23, 53, 0.09);

    @media only screen and (max-width: 640px) {
        padding: 18px;
    }
`;

const CtaIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    color: #ffffff;
    background: var(--brand);
`;

const CtaTitle = styled.h2`
    margin: 0;
    color: var(--ink);
    font-size: 1.28rem;
    line-height: 1.25;
    letter-spacing: 0;
`;

const CtaLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 16px;
    border-radius: 999px;
    color: #ffffff;
    background: linear-gradient(135deg, var(--ink), var(--brand));
    font-weight: 800;
    text-align: center;
    transition: transform 0.24s ease, box-shadow 0.24s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 16px 34px rgba(109, 63, 232, 0.18);
    }

    @media only screen and (max-width: 640px) {
        width: 100%;
    }
`;

const Content = styled.section`
    display: grid;
    gap: 24px;
    padding-top: 44px;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 20px;
    flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
    margin: 0;
    color: var(--ink);
    font-size: 2rem;
    line-height: 1.15;
    letter-spacing: 0;
`;

const CountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 36px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid rgba(32, 23, 53, 0.1);
    background: rgba(255, 255, 255, 0.78);
    color: var(--muted);
    font-size: 0.92rem;
    font-weight: 800;
`;

const PostsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 22px;

    @media only screen and (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

const PostCard = styled.article`
    display: grid;
    grid-template-rows: 220px 1fr;
    min-height: 100%;
    border: 1px solid rgba(32, 23, 53, 0.12);
    border-radius: 8px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 18px 48px rgba(32, 23, 53, 0.08);

    @media only screen and (max-width: 640px) {
        grid-template-rows: 176px 1fr;
    }
`;

const PostImageLink = styled.a`
    display: block;
    min-height: 0;
    background: var(--ink);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
    }

    &:hover img {
        transform: scale(1.03);
    }
`;

const PostBody = styled.div`
    display: grid;
    gap: 14px;
    padding: 22px;

    @media only screen and (max-width: 640px) {
        padding: 18px;
    }
`;

const MetaRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const MetaPill = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 32px;
    padding: 0 10px;
    border: 1px solid rgba(32, 23, 53, 0.1);
    border-radius: 999px;
    background: rgba(250, 247, 255, 0.8);
    color: var(--muted);
    font-size: 0.86rem;
    font-weight: 700;
`;

const PostTitle = styled.h3`
    margin: 0;
    color: var(--ink);
    font-size: 1.45rem;
    line-height: 1.2;
    letter-spacing: 0;
`;

const PostLink = styled.a`
    color: inherit;
    transition: color 0.2s ease;

    &:hover {
        color: var(--brand);
    }
`;

const PostExcerpt = styled.p`
    margin: 0;
    color: var(--muted);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.68;
`;

const ReadLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    color: var(--brand);
    font-weight: 900;
    transition: transform 0.22s ease, color 0.22s ease;

    &:hover {
        color: var(--ink);
        transform: translateX(2px);
    }
`;

const FixedCta = styled.a`
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 24;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 56px;
    padding: 0 20px;
    border-radius: 999px;
    color: #ffffff;
    background: var(--brand);
    border: 1px solid rgba(255, 255, 255, 0.78);
    box-shadow: 0 18px 46px rgba(109, 63, 232, 0.26);
    font-weight: 900;
    transition: transform 0.24s ease, box-shadow 0.24s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 22px 54px rgba(109, 63, 232, 0.34);
    }

    @media only screen and (max-width: 680px) {
        left: 16px;
        right: 16px;
        bottom: max(16px, env(safe-area-inset-bottom));
        min-height: 54px;
        padding: 0 16px;
    }
`;

function Blog() {
    const postCountLabel = BLOG_POSTS.length === 1
        ? "1 artigo publicado"
        : `${BLOG_POSTS.length} artigos publicados`;

    useEffect(() => {
        const blogImage = absoluteUrl(BLOG_POSTS[0]?.image);

        document.title = BLOG_TITLE;

        setMetaTag('meta[name="description"]', {
            name: "description",
            content: BLOG_DESCRIPTION
        });
        setMetaTag('meta[name="keywords"]', {
            name: "keywords",
            content: BLOG_KEYWORDS
        });
        setMetaTag('meta[name="robots"]', {
            name: "robots",
            content: "index, follow, max-image-preview:large"
        });
        setMetaTag('meta[property="og:title"]', {
            property: "og:title",
            content: BLOG_TITLE
        });
        setMetaTag('meta[property="og:description"]', {
            property: "og:description",
            content: BLOG_DESCRIPTION
        });
        setMetaTag('meta[property="og:type"]', {
            property: "og:type",
            content: "website"
        });
        setMetaTag('meta[property="og:site_name"]', {
            property: "og:site_name",
            content: "Mango Software House"
        });
        setMetaTag('meta[property="og:locale"]', {
            property: "og:locale",
            content: "pt_BR"
        });
        setMetaTag('meta[property="og:url"]', {
            property: "og:url",
            content: BLOG_URL
        });
        setMetaTag('meta[property="og:image"]', {
            property: "og:image",
            content: blogImage
        });
        setMetaTag('meta[property="og:image:alt"]', {
            property: "og:image:alt",
            content: "Blog da Mango sobre tecnologia, processos e operação digital"
        });
        setMetaTag('meta[name="twitter:card"]', {
            name: "twitter:card",
            content: "summary_large_image"
        });
        setMetaTag('meta[name="twitter:title"]', {
            name: "twitter:title",
            content: BLOG_TITLE
        });
        setMetaTag('meta[name="twitter:description"]', {
            name: "twitter:description",
            content: BLOG_DESCRIPTION
        });
        setMetaTag('meta[name="twitter:image"]', {
            name: "twitter:image",
            content: blogImage
        });
        setLinkTag('link[rel="canonical"]', {
            rel: "canonical",
            href: BLOG_URL
        });
        setStructuredData("blog-list-schema", {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Blog Mango",
            url: BLOG_URL,
            description: BLOG_DESCRIPTION,
            inLanguage: "pt-BR",
            publisher: {
                "@type": "Organization",
                name: "Mango Software House",
                url: SITE_URL,
                logo: {
                    "@type": "ImageObject",
                    url: `${SITE_URL}/logo.png`
                }
            },
            blogPost: BLOG_POSTS.map((post) => ({
                "@type": "BlogPosting",
                headline: post.title,
                description: post.description,
                url: `${SITE_URL}${post.url}`,
                image: absoluteUrl(post.image),
                datePublished: post.publishedAt,
                dateModified: post.modifiedAt,
                articleSection: post.category,
                keywords: post.keywords
            }))
        });

        return () => {
            removeStructuredData("blog-list-schema");
            restoreDefaultMeta();
        };
    }, []);

    return (
        <>
            <Nav />

            <Page id="top">
                <Shell>
                    <Hero>
                        <HeroCopy>
                            <Kicker>Blog Mango</Kicker>

                            <Title>Blog sobre automação, software sob medida e eficiência operacional.</Title>

                            <Summary>
                                Guias diretos para gestores que querem reduzir retrabalho, ganhar previsibilidade e transformar processos reais em sistemas, automações e rotinas digitais mais confiáveis.
                            </Summary>
                        </HeroCopy>

                        <HeroCta>
                            <CtaIcon>
                                <FactCheckRoundedIcon fontSize="small" />
                            </CtaIcon>
                            <div>
                                <CtaTitle>Quer aplicar essas ideias na sua operação?</CtaTitle>
                                <P>Receba um diagnóstico inicial para priorizar processos, sistemas ou automações com maior impacto.</P>
                            </div>
                            <CtaLink href={CAPTURE_URL}>
                                Iniciar diagnóstico
                                <ArrowOutwardRoundedIcon fontSize="small" />
                            </CtaLink>
                        </HeroCta>
                    </Hero>

                    <Content aria-labelledby="posts-title">
                        <SectionHeader>
                            <SectionTitle id="posts-title">Postagens recentes</SectionTitle>
                            <CountBadge>{postCountLabel}</CountBadge>
                        </SectionHeader>

                        <PostsGrid>
                            {BLOG_POSTS.map((post, index) => (
                                <PostCard key={post.slug}>
                                    <PostImageLink href={post.url} aria-label={`Ler: ${post.title}`}>
                                        <img
                                            src={post.image}
                                            alt={post.imageAlt}
                                            loading={index === 0 ? "eager" : "lazy"}
                                            decoding="async"
                                        />
                                    </PostImageLink>

                                    <PostBody>
                                        <MetaRow>
                                            <MetaPill>{post.category}</MetaPill>
                                            <MetaPill as="time" dateTime={post.publishedAt}>{post.dateLabel}</MetaPill>
                                            <MetaPill>{post.readingTime}</MetaPill>
                                        </MetaRow>

                                        <PostTitle>
                                            <PostLink href={post.url}>{post.title}</PostLink>
                                        </PostTitle>

                                        <PostExcerpt>{post.excerpt}</PostExcerpt>

                                        <ReadLink href={post.url}>
                                            Ler análise
                                            <ArrowOutwardRoundedIcon fontSize="small" />
                                        </ReadLink>
                                    </PostBody>
                                </PostCard>
                            ))}
                        </PostsGrid>
                    </Content>
                </Shell>
            </Page>

            <FixedCta href={CAPTURE_URL} aria-label="Solicitar diagnóstico para projeto digital">
                <FactCheckRoundedIcon fontSize="small" />
                Iniciar diagnóstico
            </FixedCta>

            <Footer />
        </>
    );
}

export { Blog };
export default Blog;
